import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import PrescriptionInterface from '../../interfaces/prescription';
import { AppState } from '../../store';
import { fetchPrescription } from '../../store/prescription/actions';
import { Action as PrescriptionAction } from '../../store/prescription/types';
import {
  DEFAULT_FULL_FILENAME,
  DEFAULT_INDIVIDUAL_FILENAME,
  downloadPrescriptionPdf,
  triggerBlobDownload,
} from '../../services/prescriptionPdf';
import { useToast } from '../../components/ui/Toast';

interface DownloadPrescriptionDocumentParams {
  loadingKey?: string | number;
  getPdfAccess: () => string | null;
  getRefreshedPdfAccess: (prescription: PrescriptionInterface) => string | null;
  errorTitle: string;
}

interface UsePrescriptionPdfDownloadResult {
  downloadFullPdf: (params: DownloadPrescriptionDocumentParams) => Promise<void>;
  downloadIndividualPdf: (params: DownloadPrescriptionDocumentParams) => Promise<void>;
  isLoading: (key?: string | number) => boolean;
}

export function usePrescriptionPdfDownload(
  prescriptionToken: string,
): UsePrescriptionPdfDownloadResult {
  const dispatch = useDispatch<ThunkDispatch<AppState, null, PrescriptionAction>>();
  const { addToast } = useToast();
  const [loadingKeys, setLoadingKeys] = useState<Array<string | number>>([]);
  const activeKeysRef = useRef<Set<string | number>>(new Set());

  const refreshPrescription = useCallback(async (): Promise<PrescriptionInterface | null> => {
    return dispatch(fetchPrescription(prescriptionToken));
  }, [dispatch, prescriptionToken]);

  const downloadPrescriptionDocument = useCallback(async (
    defaultFilename: string,
    {
      loadingKey: key = 'default',
      getPdfAccess,
      getRefreshedPdfAccess,
      errorTitle,
    }: DownloadPrescriptionDocumentParams,
  ): Promise<void> => {
    const pdfAccess = getPdfAccess();
    if (!pdfAccess) {
      return;
    }

    if (activeKeysRef.current.has(key)) {
      return;
    }
    activeKeysRef.current.add(key);
    setLoadingKeys(Array.from(activeKeysRef.current));

    try {
      const { blob, filename } = await downloadPrescriptionPdf(
        pdfAccess,
        async () => {
          const refreshedPrescription = await refreshPrescription();
          if (!refreshedPrescription) {
            return null;
          }
          return getRefreshedPdfAccess(refreshedPrescription);
        },
        { defaultFilename },
      );
      triggerBlobDownload(blob, filename);
    } catch {
      addToast({
        title: errorTitle,
        description: 'Algo deu errado ao baixar o documento',
        status: 'danger',
      });
    } finally {
      activeKeysRef.current.delete(key);
      setLoadingKeys(Array.from(activeKeysRef.current));
    }
  }, [addToast, refreshPrescription]);

  const downloadFullPdf = useCallback(
    (params: DownloadPrescriptionDocumentParams) => downloadPrescriptionDocument(DEFAULT_FULL_FILENAME, params),
    [downloadPrescriptionDocument],
  );

  const downloadIndividualPdf = useCallback(
    (params: DownloadPrescriptionDocumentParams) => downloadPrescriptionDocument(DEFAULT_INDIVIDUAL_FILENAME, params),
    [downloadPrescriptionDocument],
  );

  const isLoading = useCallback(
    (key: string | number = 'default'): boolean => loadingKeys.includes(key),
    [loadingKeys],
  );

  return {
    downloadFullPdf,
    downloadIndividualPdf,
    isLoading,
  };
}
